import React, { useContext, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";

import { Context } from "../../index";
import AccountService from "../../services/AccountService";
import { CabinetRefreshProvider, useCabinetRefresh } from "./CabinetRefresh";
import { formatFullName, initialsOf } from "./format";
import {
  AccountBanner,
  accountBannerOf,
  RefreshIndicator,
} from "./components/CabinetStates";
import "./CabinetLayout.css";

/**
 * `Ф0` (кабинет ПЗС, `АРХ-ЛК-20.10`): каркас `/cabinet/*`.
 *
 * Меню — только три раздела: Станции, Потребление, Профиль. «Карт доступа» и
 * «Документов» нет: в этап 1 они не входят. Десктоп — меню слева, телефон —
 * вкладки снизу; касание не меньше 44 px.
 *
 * Шапка: ФИО, почта, индикатор «обновлено ЧЧ:ММ:СС» и выход. Плашки учётной
 * записи — над содержимым: «поставлена на удаление до …» с отменой и «доступ
 * закрыт».
 */

const NAV_ITEMS = [
  {
    to: "/cabinet/stations",
    label: "Станции",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="5" y="3" width="10" height="18" rx="2" />
        <path d="M15 9h2a2 2 0 0 1 2 2v5a1.5 1.5 0 0 0 3 0V8l-3-3" />
        <path d="M9 8 8 11h3l-1 3" />
      </svg>
    ),
  },
  {
    to: "/cabinet/consumption",
    label: "Потребление",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
      </svg>
    ),
  },
  {
    to: "/cabinet/profile",
    label: "Профиль",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </svg>
    ),
  },
];

function CabinetNav({ className }: { className: string }) {
  return (
    <nav className={className} aria-label="Разделы кабинета">
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            `cabinet-nav__link${isActive ? " cabinet-nav__link--active" : ""}`
          }
        >
          <span className="cabinet-nav__icon">{item.icon}</span>
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function CabinetUser({ compact = false }: { compact?: boolean }) {
  const { store } = useContext(Context);
  const user = store?.user;

  return (
    <div className={`cabinet-user${compact ? " cabinet-user--compact" : ""}`}>
      <span className="cabinet-user__avatar" aria-hidden="true">
        {initialsOf(user)}
      </span>
      <span className="cabinet-user__text">
        <b className="cabinet-user__name">{formatFullName(user)}</b>
        <span className="cabinet-user__email">{user?.email || "—"}</span>
      </span>
    </div>
  );
}

const CabinetLayoutInner = observer(function CabinetLayoutInner() {
  const { store } = useContext(Context);
  const navigate = useNavigate();
  const { updatedAt, failed, refresh } = useCabinetRefresh();
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const banner = accountBannerOf(store?.user);

  const cancelDeletion = async () => {
    setCancelling(true);
    setCancelError(null);

    try {
      await AccountService.cancelDeletion();
      // Состояние и срок берутся заново: плашка снимается по ответу сервера, а
      // не по локальной догадке.
      await store.loadMyProfile();
    } catch {
      setCancelError("Не удалось отменить удаление. Попробуйте ещё раз.");
    } finally {
      setCancelling(false);
    }
  };

  const logout = async () => {
    await store.logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="cabinet">
      <div className="cabinet-shell">
        <aside className="cabinet-sidebar">
          <div className="cabinet-brand">
            <span className="cabinet-brand__mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
              </svg>
            </span>
            <span className="cabinet-brand__text">
              <b>BMA</b>
              <span>Личный кабинет</span>
            </span>
          </div>

          <CabinetNav className="cabinet-nav cabinet-nav--sidebar" />

          <div className="cabinet-sidebar__footer">
            <button type="button" className="cabinet-btn" onClick={logout}>
              Выйти
            </button>
          </div>
        </aside>

        <div className="cabinet-main">
          <header className="cabinet-header">
            <div className="cabinet-header__eyebrow">Личный кабинет</div>
            <div className="cabinet-header__right">
              <RefreshIndicator updatedAt={updatedAt} failed={failed} onRetry={refresh} />
              <CabinetUser />
              <button
                type="button"
                className="cabinet-btn cabinet-btn--icon cabinet-logout"
                onClick={logout}
                aria-label="Выйти"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                </svg>
              </button>
            </div>
          </header>

          <main className="cabinet-content">
            {banner ? (
              <AccountBanner
                model={banner}
                onCancelDeletion={cancelDeletion}
                cancelling={cancelling}
                error={cancelError}
              />
            ) : null}
            <Outlet />
          </main>
        </div>
      </div>

      <CabinetNav className="cabinet-nav cabinet-tabbar" />
    </div>
  );
});

export default function CabinetLayout() {
  return (
    <CabinetRefreshProvider>
      <CabinetLayoutInner />
    </CabinetRefreshProvider>
  );
}
